<script setup>
import MatchGroupHeader from '@/components/Match/MatchGroupHeader.vue';
import MatchListItem from '@/components/Match/MatchListItem.vue';
import PageTitleSection from '@/components/PageTitleSection.vue';
import { useFixturesStore } from '@/stores/fixtures';
import { storeToRefs } from 'pinia';
import { computed, ref, watch, onUnmounted, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import mainMarkets from '@/mainMarkets'
import { useI18n } from 'vue-i18n';

const { t, te } = useI18n()
const fixtures = useFixturesStore()
const { getCompetition, eventsByCompetition } = storeToRefs(fixtures)
const visibleEvents = ref(new Set());
const selectionsMap = ref({})
let observer;
const route = useRoute()
const ci = computed(() => route.params.ci)
const si = computed(() => route.params.si)
const eventsByDate = computed(() => {
    const groups = {};
    for (const key in eventsByCompetition.value(ci.value)) {
        const event = eventsByCompetition.value(ci.value)[key];
        if (event.o == 'true') {
            if (!groups['Outright']) {
                groups['Outright'] = [];
            }
            groups['Outright'].push(event);
        }  else{
            const { t } = event;

            const date = new Date(t);
            const dateString = date.toISOString().split('T')[0];

            if (!groups[dateString]) {
                groups[dateString] = [];
            }

            groups[dateString].push(event);
        }
    }

    for (const date in groups) {
        groups[date].sort((a, b) => new Date(a.t) - new Date(b.t));
    }

    // Sort by date 
    const sortedGroups = {};
    Object.keys(groups)
        .sort((a, b) => new Date(a) - new Date(b))
        .forEach(date => {
            sortedGroups[date] = groups[date];
        });

    return sortedGroups;
});

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const eventId =  Number(entry.target.dataset.i);
        if (entry.isIntersecting) {
          visibleEvents.value.add(eventId);
        } else {
          visibleEvents.value.delete(eventId);
        }
      });
    },
    { root: null, threshold: 0.1 }
  );

  document.querySelectorAll(".event-item").forEach((el) => {
    observer.observe(el);
  });
});
onUnmounted(() => {
  if (observer) observer.disconnect();
});
const preMarketId = computed(() => {
  return mainMarkets[si.value]?.[0] || null
})
const loadSelections = async () => {
  if (visibleEvents.value.size === 0) return

  const preMarketId = mainMarkets[si.value]?.[0]

  let markets = undefined
  if (preMarketId) {
    markets = [preMarketId]
  }

  const res = await fixtures.fetchSelections(
    Array.from(visibleEvents.value),
    "pre",
    markets 
  )

  for (const ev of res) {
    selectionsMap.value[ev.fi] = ev.m || []
  }
}
watch(visibleEvents, () => {
  loadSelections()
}, { deep: true })

let competitionName = te(`competitions.${ci.value}`)
  ? t(`competitions.${ci.value}`)
  : getCompetition.value(ci.value)?.cn
</script>
<template>
  <div class="flex flex-1">
    <div class="flex-1">
      <PageTitleSection :title="competitionName"/>
      <div class="bg-[#383838] rounded">
        <div v-for="events, date in eventsByDate">
          <MatchGroupHeader :title="date" :sport="si" />

          <ul class="list-none divide-y divide-neutral-600/50">
            <li 
              v-for="event in events"
              :key="event.i"
              class="event-item"
              :data-i="event.i"
            >
              <MatchListItem
                :event="event"
                :selections="selectionsMap[event.i] || []"
                :market="preMarketId" 
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
